import {
  AppWindow,
  ArrowRight,
  BadgeCheck,
  Cpu,
  Globe2,
  LifeBuoy,
  ServerCog,
  SlidersHorizontal,
} from "lucide-react";
import { PageBreadcrumb } from "@/components/PageBreadcrumb";
import { SiteLayout } from "@/components/SiteLayout";
import { Button } from "@/components/ui/button";
import { useSite } from "@/hooks/use-site";
import { localizeUrl } from "@/lib/site";
import heroDeviceLineup from "@/assets/about/hero-device-lineup.jpg";
import almatvWhite from "@/assets/clients/almatv-white.jpg";
import megogoBox from "@/assets/clients/megogo-box.jpg";
import kyivstar from "@/assets/clients/kyivstar.jpg";
import sarkor from "@/assets/clients/sarkor.jpg";
import sweettv from "@/assets/clients/sweettv.jpg";
import metrotv from "@/assets/clients/metrotv.jpg";

export function AboutPage() {
  const { messages, site, href } = useSite();
  const contactSales = localizeUrl("/contacts?inquiry=sales#inquiry-form", site.locale, href);
  const products = localizeUrl("/#lineup", site.locale, href);
  const customization = localizeUrl("/solutions/customization", site.locale, href);
  const alcatraz = localizeUrl("/solutions/alcatraz-dms", site.locale, href);
  const clientsAnchor = localizeUrl("/#clients", site.locale, href);
  const brandResources = localizeUrl("/brand", site.locale, href);

  const stats = [
    [messages.aboutStat1Value, messages.aboutStat1Label],
    [messages.aboutStat3Value, messages.aboutStat3Label],
    [messages.aboutStat4Value, messages.aboutStat4Label],
    [messages.aboutStat5Value, messages.aboutStat5Label],
  ];

  const clients = [
    { img: kyivstar, name: "Kyivstar TB" },
    { img: megogoBox, name: "MEGOGO" },
    { img: sweettv, name: "SWEET.TV" },
    { img: almatvWhite, name: "Alma TV" },
    { img: sarkor, name: "Sarkor Telecom" },
    { img: metrotv, name: "Metro TV" },
  ];

  const capabilities = [
    {
      icon: Cpu,
      title: messages.aboutCapabilityHardwareTitle,
      body: messages.aboutCapabilityHardwareBody,
      cta: messages.aboutCapabilityHardwareCta,
      href: products,
    },
    {
      icon: AppWindow,
      title: messages.aboutCapabilitySoftwareTitle,
      body: messages.aboutCapabilitySoftwareBody,
      cta: messages.aboutCapabilitySoftwareCta,
      href: customization,
    },
    {
      icon: ServerCog,
      title: messages.aboutCapabilityOperationsTitle,
      body: messages.aboutCapabilityOperationsBody,
      cta: messages.aboutCapabilityOperationsCta,
      href: alcatraz,
    },
  ];

  const principles = [
    {
      icon: BadgeCheck,
      title: messages.aboutPrinciple1Title,
      body: messages.aboutPrinciple1Body,
    },
    {
      icon: SlidersHorizontal,
      title: messages.aboutPrinciple2Title,
      body: messages.aboutPrinciple2Body,
    },
    {
      icon: LifeBuoy,
      title: messages.aboutPrinciple3Title,
      body: messages.aboutPrinciple3Body,
    },
  ];

  return (
    <SiteLayout>
      <PageBreadcrumb items={[{ label: messages.navAbout }]} />

      {/* Hero */}
      <section className="relative overflow-hidden border-b border-border/60">
        <div
          aria-hidden
          className="bg-grid pointer-events-none absolute inset-0 -z-10 opacity-50"
        />
        <div
          aria-hidden
          className="pointer-events-none absolute left-1/2 top-0 -z-10 h-[520px] w-[900px] -translate-x-1/2 rounded-full opacity-45 blur-3xl"
          style={{ background: "var(--gradient-aurora)" }}
        />
        <div className="mx-auto max-w-7xl px-6 py-20 lg:py-32">
          <div className="max-w-5xl animate-fade-up">
            <div className="flex flex-wrap items-center gap-3 text-[10px] font-semibold uppercase tracking-[0.2em] text-primary">
              <span>{messages.aboutEyebrow}</span>
              <span className="h-px w-6 bg-primary/30" aria-hidden />
              <span className="rounded-full border border-border/60 bg-card/60 px-2.5 py-1 text-muted-foreground">
                {messages.aboutHeroEst}
              </span>
            </div>
            <h1 className="mt-7 max-w-4xl text-4xl font-semibold leading-[1.04] tracking-tight sm:text-6xl lg:text-7xl">
              {messages.aboutHeading}
            </h1>
            <p className="mt-7 max-w-3xl text-lg leading-relaxed text-muted-foreground sm:text-xl">
              {messages.aboutIntro}
            </p>
            <div className="mt-9 flex flex-col gap-3 sm:flex-row">
              <Button asChild size="lg" className="h-11 rounded-lg px-6 font-medium animate-glow">
                <a href={contactSales}>
                  {messages.contactSales}
                  <ArrowRight className="ml-1 h-4 w-4" />
                </a>
              </Button>
              <Button
                asChild
                size="lg"
                variant="outline"
                className="h-11 rounded-lg px-6 font-medium"
              >
                <a href={products}>{messages.exploreProducts}</a>
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Scale */}
      <section className="relative border-b border-border/60 bg-card/40">
        <div className="mx-auto max-w-7xl px-6">
          <dl className="grid grid-cols-2 divide-x divide-y divide-border/60 lg:grid-cols-4 lg:divide-y-0">
            {stats.map(([value, label], index) => (
              <div
                key={label}
                className="group px-2 py-9 text-center animate-fade-up sm:px-10 sm:text-left"
                style={{ animationDelay: `${index * 80}ms` }}
              >
                <dt className="text-4xl font-semibold tracking-tight text-gradient sm:text-5xl">
                  {value}
                </dt>
                <dd className="mt-2 text-xs font-medium uppercase tracking-widest text-muted-foreground">
                  {label}
                </dd>
              </div>
            ))}
          </dl>
        </div>
      </section>

      {/* Operator proof */}
      <section className="border-b border-border/60">
        <div className="mx-auto max-w-7xl px-6 py-20 lg:py-24">
          <div className="flex flex-col items-start justify-between gap-4 sm:flex-row sm:items-end">
            <div className="max-w-xl animate-fade-up">
              <p className="text-xs font-semibold uppercase tracking-widest text-primary">
                {messages.aboutClientsEyebrow}
              </p>
              <p className="mt-3 text-base leading-relaxed text-muted-foreground">
                {messages.aboutClientsLede}
              </p>
            </div>
            <a
              href={clientsAnchor}
              className="group inline-flex items-center gap-1.5 text-sm font-medium text-primary hover:underline"
            >
              {messages.aboutClientsCta}
              <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" />
            </a>
          </div>

          <ul className="mt-10 grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-6">
            {clients.map(({ img, name }, index) => (
              <li
                key={name}
                className="group relative overflow-hidden rounded-xl border border-border/60 bg-card/70 animate-fade-up"
                style={{ animationDelay: `${index * 50}ms` }}
              >
                <div className="relative aspect-[4/3] overflow-hidden">
                  <img
                    src={img}
                    alt={`${name} - ${messages.aboutClientImageAltSuffix}`}
                    loading="lazy"
                    className="h-full w-full object-cover transition-all duration-500 group-hover:scale-105"
                  />
                </div>
                <div className="border-t border-border/60 px-3 py-2 text-center text-[11px] font-medium tracking-wide text-muted-foreground">
                  {name}
                </div>
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* Capabilities */}
      <section className="relative border-b border-border/60 bg-muted/30">
        <div
          aria-hidden
          className="pointer-events-none absolute left-1/2 top-0 h-[420px] w-[760px] -translate-x-1/2 rounded-full bg-primary/8 blur-3xl"
        />
        <div className="relative mx-auto max-w-7xl px-6 py-24 lg:py-32">
          <div className="max-w-3xl animate-fade-up">
            <p className="text-xs font-semibold uppercase tracking-widest text-primary">
              {messages.aboutCapabilitiesEyebrow}
            </p>
            <h2 className="mt-4 text-3xl font-semibold tracking-tight sm:text-5xl">
              {messages.aboutCapabilitiesTitle}
            </h2>
            <p className="mt-5 max-w-2xl text-base leading-relaxed text-muted-foreground">
              {messages.aboutCapabilitiesLede}
            </p>
          </div>

          <div className="mt-12 grid grid-cols-1 gap-5 md:grid-cols-3">
            {capabilities.map(({ icon: Icon, title, body, cta, href: linkHref }, index) => (
              <a
                key={title}
                href={linkHref}
                className="group relative flex flex-col overflow-hidden rounded-2xl border border-border/60 bg-card/80 p-7 shadow-[var(--shadow-soft)] transition-all duration-500 animate-fade-up hover:-translate-y-1 hover:border-primary/30 hover:shadow-glow"
                style={{ animationDelay: `${index * 80}ms` }}
              >
                <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-gradient-to-br from-primary/15 to-primary/5 text-primary ring-1 ring-primary/10 transition-transform duration-500 group-hover:scale-110 group-hover:rotate-3">
                  <Icon className="h-5 w-5" />
                </div>
                <h3 className="mt-6 text-xl font-semibold tracking-tight">{title}</h3>
                <p className="mt-3 flex-1 text-sm leading-relaxed text-muted-foreground">{body}</p>
                <span className="mt-7 inline-flex items-center gap-1.5 text-sm font-medium text-primary">
                  {cta}
                  <ArrowRight className="h-3.5 w-3.5 transition-transform group-hover:translate-x-0.5" />
                </span>
              </a>
            ))}
          </div>

          <p className="mt-10 flex items-start gap-2.5 text-sm text-muted-foreground animate-fade-up sm:items-center">
            <BadgeCheck aria-hidden className="mt-0.5 h-4 w-4 shrink-0 text-primary sm:mt-0" />
            {messages.verimatrixNote}
          </p>
        </div>
      </section>

      {/* Company story */}
      <section className="border-b border-border/60">
        <div className="mx-auto grid max-w-7xl grid-cols-1 items-center gap-14 px-6 py-24 lg:grid-cols-12 lg:gap-20 lg:py-32">
          <div className="relative lg:col-span-6 animate-fade-up">
            <div
              aria-hidden
              className="pointer-events-none absolute -inset-8 -z-10 rounded-[2rem] opacity-45 blur-3xl"
              style={{ background: "var(--gradient-aurora)" }}
            />
            <div className="overflow-hidden rounded-2xl border border-border bg-card shadow-glow ring-1 ring-border/40">
              <img
                src={heroDeviceLineup}
                alt={messages.aboutHeroImageAlt}
                className="block h-auto w-full object-cover"
                loading="lazy"
              />
            </div>
          </div>

          <div className="lg:col-span-6 animate-fade-up" style={{ animationDelay: "100ms" }}>
            <p className="text-xs font-semibold uppercase tracking-widest text-primary">
              {messages.aboutHeritageEyebrow}
            </p>
            <h2 className="mt-4 text-3xl font-semibold tracking-tight sm:text-5xl">
              {messages.aboutHeritageTitle}
            </h2>
            <div className="mt-7 space-y-5">
              <p className="text-lg leading-relaxed text-foreground">{messages.aboutStoryPara1}</p>
              <p className="text-base leading-relaxed text-muted-foreground">
                {messages.aboutStoryPara2}
              </p>
            </div>
            <aside className="mt-8 border-l border-primary/50 pl-5">
              <div className="inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-widest text-primary">
                <Globe2 className="h-4 w-4" />
                {messages.aboutWorldwideDeliveryTitle}
              </div>
              <p className="mt-3 max-w-xl text-base leading-relaxed text-foreground/85">
                {messages.aboutWorldwideDeliveryBody}
              </p>
            </aside>
            <div className="mt-8 border-t border-border/60 pt-6">
              <p className="text-sm text-muted-foreground">{messages.aboutBrandResourcesPrompt}</p>
              <a
                href={brandResources}
                className="group mt-2 inline-flex items-center gap-1.5 text-sm font-medium text-primary hover:underline"
              >
                {messages.aboutBrandResourcesCta}
                <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" />
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Working principles */}
      <section className="border-b border-border/60 bg-card/40">
        <div className="mx-auto max-w-7xl px-6 py-24 lg:py-32">
          <div className="max-w-3xl animate-fade-up">
            <p className="text-xs font-semibold uppercase tracking-widest text-primary">
              {messages.aboutPrinciplesEyebrow}
            </p>
            <h2 className="mt-4 text-3xl font-semibold tracking-tight sm:text-5xl">
              {messages.aboutPrinciplesTitle}
            </h2>
            <p className="mt-5 max-w-2xl text-base leading-relaxed text-muted-foreground">
              {messages.aboutPrinciplesLede}
            </p>
          </div>

          <div className="mt-12 grid grid-cols-1 gap-8 md:grid-cols-3">
            {principles.map(({ icon: Icon, title, body }, index) => (
              <div
                key={title}
                className="border-t border-border/70 pt-6 animate-fade-up"
                style={{ animationDelay: `${index * 80}ms` }}
              >
                <Icon className="h-5 w-5 text-primary" />
                <h3 className="mt-5 text-lg font-semibold tracking-tight">{title}</h3>
                <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="relative overflow-hidden bg-foreground text-background">
        <div
          aria-hidden
          className="pointer-events-none absolute inset-0 opacity-30 blur-3xl"
          style={{ background: "var(--gradient-aurora)" }}
        />
        <div className="relative mx-auto max-w-4xl px-6 py-24 text-center lg:py-28">
          <div className="inline-flex items-center gap-2 rounded-full border border-background/20 bg-background/5 px-3 py-1 text-xs text-background/80 backdrop-blur">
            <Globe2 className="h-3.5 w-3.5 text-primary" />
            {messages.aboutCtaEyebrow}
          </div>
          <h2 className="mt-6 text-3xl font-semibold tracking-tight sm:text-5xl">
            {messages.aboutCtaTitle}
          </h2>
          <p className="mx-auto mt-5 max-w-2xl text-base leading-relaxed text-background/70">
            {messages.aboutCtaBody}
          </p>
          <div className="mt-10">
            <Button asChild size="lg" className="h-12 rounded-lg px-8 font-medium">
              <a href={contactSales}>
                {messages.contactSales}
                <ArrowRight className="ml-1 h-4 w-4" />
              </a>
            </Button>
          </div>
        </div>
      </section>
    </SiteLayout>
  );
}
